/**
 * Five-minute StorySplat asset lease renewal.
 *
 * The storage Worker never redirects an expired bearer. It returns a 401 plus
 * the non-secret `X-Asset-Lease: expired` signal. Browser code sends only the
 * encrypted ticket in a POST body to the trusted Discover endpoint, validates
 * the returned URL is the exact same object identity, and retries GET/HEAD.
 */

export const PROD_ASSET_RENEWAL_ENDPOINT = 'https://discover.storysplat.com/api/asset-access/renew';
const RENEW_PATH = '/api/asset-access/renew';
const SAFE_ID = /^[a-zA-Z0-9._-]{1,250}$/;
const SAFE_TICKET = /^[a-zA-Z0-9_-]{38,8192}$/;
const PREEMPTIVE_RENEW_WINDOW_MS = 10_000;
const MAX_RENEW_RESPONSE_BYTES = 16 * 1024;
const MAX_FRESH_LEASE_MS = 5 * 60 * 1000 + 30_000;

export interface AssetLeaseOptions {
    sceneId: string;
    ownerId: string;
    /** Exact expiry returned by the trusted scene API. */
    expiresAtMs: number;
    /** @internal Same-origin path, or the fixed production Discover endpoint. */
    renewalEndpoint?: string;
}

export interface AssetLeaseHandle {
    dispose(): void;
}

interface LeaseEntry extends AssetLeaseOptions {
    endpoint: string;
    seq: number;
    disposed: boolean;
}

interface RenewedMapping {
    url: string;
    expiresAtMs: number;
}

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

const entries = new Set<LeaseEntry>();
const renewedUrls = new Map<string, RenewedMapping>();
const renewalsInFlight = new Map<string, Promise<RenewedMapping>>();
let entrySeq = 0;
let interceptorsInstalled = false;
let renewalFetch: FetchLike | null = null;

function urlString(input: RequestInfo | URL): string {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    return input.url;
}

function parsedTicketUrl(raw: string): { url: URL; ticket: string } | null {
    try {
        const url = new URL(raw, typeof window !== 'undefined' ? window.location.href : undefined);
        const tickets = url.searchParams.getAll('ss_access');
        if (
            (url.protocol !== 'https:' && url.protocol !== 'http:')
            || url.username
            || url.password
            || tickets.length !== 1
            || !SAFE_TICKET.test(tickets[0])
            || url.searchParams.get('alt') !== 'media'
            || url.searchParams.has('token')
        ) return null;
        return { url, ticket: tickets[0] };
    } catch {
        return null;
    }
}

/** @internal exported for focused security tests. */
export function trustedAssetRenewalEndpoint(raw: string | undefined, pageHref: string): string {
    const page = new URL(pageHref);
    const endpoint = new URL(raw || RENEW_PATH, page);
    if (
        endpoint.username
        || endpoint.password
        || endpoint.search
        || endpoint.hash
        || endpoint.pathname !== RENEW_PATH
    ) throw new Error('Untrusted asset lease renewal endpoint');
    const sameOrigin = endpoint.origin === page.origin;
    const fixedProduction = endpoint.toString() === PROD_ASSET_RENEWAL_ENDPOINT;
    if (!sameOrigin && !fixedProduction) throw new Error('Untrusted asset lease renewal endpoint');
    if (endpoint.protocol !== 'https:' && page.hostname !== 'localhost' && page.hostname !== '127.0.0.1') {
        throw new Error('Asset lease renewal requires HTTPS');
    }
    return endpoint.toString();
}

function sameObjectIdentity(previousRaw: string, renewedRaw: string): boolean {
    const previous = parsedTicketUrl(previousRaw);
    const renewed = parsedTicketUrl(renewedRaw);
    if (!previous || !renewed) return false;
    const allowedKeys = new Set(['alt', 'generation', 'ss_access']);
    if ([...renewed.url.searchParams.keys()].some(key => !allowedKeys.has(key))) return false;
    return renewed.url.origin === previous.url.origin
        && renewed.url.pathname === previous.url.pathname
        && renewed.url.searchParams.get('alt') === 'media'
        && (renewed.url.searchParams.get('generation') || '')
            === (previous.url.searchParams.get('generation') || '');
}

function currentMapping(raw: string): RenewedMapping | null {
    let current = raw;
    let latest: RenewedMapping | null = null;
    for (let index = 0; index < 8; index++) {
        const mapped = renewedUrls.get(current);
        if (!mapped || mapped.url === current) break;
        latest = mapped;
        current = mapped.url;
    }
    return latest;
}

function currentUrl(raw: string): string {
    return currentMapping(raw)?.url || raw;
}

function selectEntry(raw: string): LeaseEntry | null {
    const parsed = parsedTicketUrl(raw);
    if (!parsed) return null;
    let decoded = parsed.url.pathname;
    try { decoded = decodeURIComponent(decoded); } catch { /* use encoded spelling */ }
    let best: LeaseEntry | null = null;
    let bestScore = -1;
    for (const entry of entries) {
        if (entry.disposed) continue;
        const ownerMatch = decoded.includes(`/users/${entry.ownerId}/`)
            || decoded.includes(`users/${entry.ownerId}/`)
            || parsed.url.pathname.includes(`users%2F${encodeURIComponent(entry.ownerId)}%2F`);
        const publicPath = decoded.includes('/public/') || decoded.startsWith('/public/');
        if (!ownerMatch && !publicPath) continue;
        const sceneMatch = decoded.includes(`/${entry.sceneId}/`)
            || decoded.includes(`/${entry.sceneId}.`);
        const score = sceneMatch ? 2 : ownerMatch ? 1 : 0;
        if (score > bestScore || (score === bestScore && (!best || entry.seq > best.seq))) {
            best = entry;
            bestScore = score;
        }
    }
    return best;
}

function shouldRenewBeforeRequest(raw: string, entry: LeaseEntry, nowMs = Date.now()): boolean {
    const mapping = currentMapping(raw);
    const expiresAtMs = mapping?.expiresAtMs ?? entry.expiresAtMs;
    return expiresAtMs <= nowMs + PREEMPTIVE_RENEW_WINDOW_MS;
}

async function readRenewalResponse(response: Response): Promise<unknown> {
    const declared = Number(response.headers.get('content-length') || 0);
    if (Number.isFinite(declared) && declared > MAX_RENEW_RESPONSE_BYTES) {
        await response.body?.cancel().catch(() => undefined);
        throw new Error('Invalid asset lease response');
    }
    const text = await response.text();
    if (new TextEncoder().encode(text).byteLength > MAX_RENEW_RESPONSE_BYTES) {
        throw new Error('Invalid asset lease response');
    }
    return JSON.parse(text);
}

/** @internal exported for focused security tests. */
export async function renewAssetLease(
    previousUrl: string,
    endpoint: string,
    fetchImpl: FetchLike,
    pageHref: string,
): Promise<RenewedMapping> {
    const previous = parsedTicketUrl(previousUrl);
    if (!previous) throw new Error('Invalid expired asset lease');
    const trustedEndpoint = trustedAssetRenewalEndpoint(endpoint, pageHref);
    const existing = renewalsInFlight.get(previous.ticket);
    if (existing) return existing;

    const pending = (async () => {
        const endpointUrl = new URL(trustedEndpoint);
        const pageUrl = new URL(pageHref);
        const response = await fetchImpl(trustedEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ssAccess: previous.ticket }),
            cache: 'no-store',
            credentials: endpointUrl.origin === pageUrl.origin ? 'include' : 'omit',
            redirect: 'error',
            referrerPolicy: 'no-referrer',
        });
        if (!response.ok) throw new Error('Asset lease renewal denied');
        const value = await readRenewalResponse(response) as {
            url?: unknown;
            expiresAtMs?: unknown;
        };
        const nowMs = Date.now();
        if (
            typeof value?.url !== 'string'
            || typeof value.expiresAtMs !== 'number'
            || !Number.isSafeInteger(value.expiresAtMs)
            || value.expiresAtMs <= nowMs
            || value.expiresAtMs > nowMs + MAX_FRESH_LEASE_MS
            || !sameObjectIdentity(previousUrl, value.url)
        ) throw new Error('Invalid asset lease response');
        const mapping = { url: value.url, expiresAtMs: value.expiresAtMs };
        renewedUrls.set(previousUrl, mapping);
        return mapping;
    })().finally(() => renewalsInFlight.delete(previous.ticket));
    renewalsInFlight.set(previous.ticket, pending);
    return pending;
}

function replaceFetchInput(input: RequestInfo | URL, url: string): RequestInfo | URL {
    if (typeof input === 'string') return url;
    if (input instanceof URL) return new URL(url);
    return new Request(url, input);
}

function requestMethod(input: RequestInfo | URL, init?: RequestInit): string {
    return (init?.method || (typeof Request !== 'undefined' && input instanceof Request ? input.method : 'GET')).toUpperCase();
}

function isExpiredLeaseResponse(response: Response): boolean {
    return response.status === 401 && response.headers.get('x-asset-lease') === 'expired';
}

async function fetchWithRenewal(
    nativeFetch: FetchLike,
    input: RequestInfo | URL,
    init?: RequestInit,
): Promise<Response> {
    const method = requestMethod(input, init);
    if (method !== 'GET' && method !== 'HEAD') return nativeFetch(input, init);
    const originalUrl = urlString(input);
    const entry = selectEntry(originalUrl);
    if (!entry || !renewalFetch || typeof window === 'undefined') return nativeFetch(input, init);

    let effectiveUrl = currentUrl(originalUrl);
    if (shouldRenewBeforeRequest(originalUrl, entry)) {
        try {
            effectiveUrl = (await renewAssetLease(
                effectiveUrl,
                entry.endpoint,
                renewalFetch,
                window.location.href,
            )).url;
        } catch {
            // Let the Worker return its ordinary fail-closed 401/403 response.
        }
    }

    let response = await nativeFetch(replaceFetchInput(input, effectiveUrl), init);
    if (!isExpiredLeaseResponse(response)) return response;
    await response.body?.cancel().catch(() => undefined);
    try {
        const renewed = await renewAssetLease(
            effectiveUrl,
            entry.endpoint,
            renewalFetch,
            window.location.href,
        );
        response = await nativeFetch(replaceFetchInput(input, renewed.url), init);
    } catch {
        // Return a fresh generic denial without exposing the expired response body.
        return new Response('Asset lease expired', {
            status: 401,
            headers: { 'Cache-Control': 'private, no-store' },
        });
    }
    return response;
}

function installFetchInterceptor(): void {
    if (typeof window.fetch !== 'function') return;
    const nativeFetch = window.fetch.bind(window) as FetchLike;
    renewalFetch = nativeFetch;
    window.fetch = ((input: RequestInfo | URL, init?: RequestInit) =>
        fetchWithRenewal(nativeFetch, input, init)) as typeof window.fetch;
}

interface XhrLeaseState {
    seq: number;
    url: string;
    openArgs: unknown[];
    headers: Array<[string, string]>;
    aborted: boolean;
}

function installXhrInterceptor(): void {
    const proto = window.XMLHttpRequest?.prototype;
    if (!proto || !renewalFetch) return;
    const nativeOpen = proto.open;
    const nativeSend = proto.send;
    const nativeAbort = proto.abort;
    const nativeSetRequestHeader = proto.setRequestHeader;
    const states = new WeakMap<XMLHttpRequest, XhrLeaseState>();

    proto.open = function leaseOpen(this: XMLHttpRequest, ...args: unknown[]) {
        const raw = typeof args[1] === 'string' ? args[1] : args[1] instanceof URL ? args[1].href : '';
        const previous = states.get(this);
        const state: XhrLeaseState = {
            seq: (previous?.seq ?? 0) + 1,
            url: raw,
            openArgs: [...args],
            headers: [],
            aborted: false,
        };
        states.set(this, state);
        const effectiveArgs = [...args];
        if (raw) effectiveArgs[1] = currentUrl(raw);
        return (nativeOpen as (...values: unknown[]) => void).apply(this, effectiveArgs);
    } as typeof proto.open;

    proto.setRequestHeader = function leaseSetRequestHeader(this: XMLHttpRequest, name: string, value: string) {
        states.get(this)?.headers.push([name, value]);
        return nativeSetRequestHeader.call(this, name, value);
    };

    proto.abort = function leaseAbort(this: XMLHttpRequest) {
        const state = states.get(this);
        if (state) {
            state.aborted = true;
            state.seq++;
        }
        return nativeAbort.call(this);
    };

    proto.send = function leaseSend(this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
        const state = states.get(this);
        const entry = state?.url ? selectEntry(state.url) : null;
        const asyncRequest = state?.openArgs[2] !== false;
        if (!state || !entry || !asyncRequest || !shouldRenewBeforeRequest(state.url, entry)) {
            return nativeSend.call(this, body);
        }

        const requestSeq = state.seq;
        const withCredentials = this.withCredentials;
        const timeout = this.timeout;
        const responseType = this.responseType;
        void renewAssetLease(
            currentUrl(state.url),
            entry.endpoint,
            renewalFetch as FetchLike,
            window.location.href,
        ).then((renewed) => {
            const current = states.get(this);
            if (!current || current.aborted || current.seq !== requestSeq) return;
            const args = [...current.openArgs];
            args[1] = renewed.url;
            (nativeOpen as (...values: unknown[]) => void).apply(this, args);
            this.withCredentials = withCredentials;
            this.timeout = timeout;
            try { this.responseType = responseType; } catch { /* browser-specific */ }
            for (const [name, value] of current.headers) nativeSetRequestHeader.call(this, name, value);
            nativeSend.call(this, body);
        }).catch(() => {
            const current = states.get(this);
            if (!current || current.aborted || current.seq !== requestSeq) return;
            nativeSend.call(this, body);
        });
        return undefined;
    } as typeof proto.send;
}

function installElementLeaseInterceptor(
    prototype: object,
    mediaElement: boolean,
): void {
    const descriptor = Object.getOwnPropertyDescriptor(prototype, 'src');
    if (!descriptor?.get || !descriptor.set || descriptor.configurable === false) return;
    const retried = new WeakMap<EventTarget, Set<string>>();
    Object.defineProperty(prototype, 'src', {
        ...descriptor,
        get: descriptor.get,
        set(this: HTMLImageElement | HTMLMediaElement, raw: string) {
            const initial = typeof raw === 'string' ? raw : String(raw);
            const effective = currentUrl(initial);
            descriptor.set!.call(this, effective);
            if (!parsedTicketUrl(effective)) return;
            this.addEventListener('error', () => {
                const failed = descriptor.get!.call(this) as string;
                const entry = selectEntry(failed);
                if (!entry || !renewalFetch) return;
                const ticket = parsedTicketUrl(failed)?.ticket;
                if (!ticket) return;
                const attempts = retried.get(this) || new Set<string>();
                if (attempts.has(ticket)) return;
                attempts.add(ticket);
                retried.set(this, attempts);
                const wasPaused = mediaElement ? (this as HTMLMediaElement).paused : true;
                const currentTime = mediaElement ? (this as HTMLMediaElement).currentTime : 0;
                void renewAssetLease(failed, entry.endpoint, renewalFetch, window.location.href)
                    .then((renewed) => {
                        // Re-enter the patched setter so the fresh lease also
                        // gets its own one-shot expiry recovery listener.
                        this.src = renewed.url;
                        if (!mediaElement) return;
                        const media = this as HTMLMediaElement;
                        media.addEventListener('loadedmetadata', () => {
                            if (Number.isFinite(currentTime) && currentTime > 0) {
                                try { media.currentTime = currentTime; } catch { /* non-seekable */ }
                            }
                            if (!wasPaused) void media.play().catch(() => undefined);
                        }, { once: true });
                        media.load();
                    })
                    .catch(() => undefined);
            }, { once: true });
        },
    });
}

function installInterceptors(): void {
    if (interceptorsInstalled || typeof window === 'undefined') return;
    interceptorsInstalled = true;
    installFetchInterceptor();
    installXhrInterceptor();
    try {
        if (window.HTMLImageElement) installElementLeaseInterceptor(window.HTMLImageElement.prototype, false);
        if (window.HTMLMediaElement) installElementLeaseInterceptor(window.HTMLMediaElement.prototype, true);
    } catch {
        // Locked-down DOM prototypes: fetch/XHR coverage remains available.
    }
}

export function createAssetLeaseManager(options: AssetLeaseOptions): AssetLeaseHandle {
    if (!SAFE_ID.test(options.sceneId) || !SAFE_ID.test(options.ownerId)) {
        throw new Error('Invalid asset lease attribution');
    }
    if (!Number.isSafeInteger(options.expiresAtMs) || options.expiresAtMs <= 0) {
        throw new Error('Invalid asset lease expiry');
    }
    if (typeof window === 'undefined') return { dispose() {} };
    const endpoint = trustedAssetRenewalEndpoint(options.renewalEndpoint, window.location.href);
    const entry: LeaseEntry = {
        ...options,
        endpoint,
        seq: entrySeq++,
        disposed: false,
    };
    installInterceptors();
    entries.add(entry);
    return {
        dispose(): void {
            if (entry.disposed) return;
            entry.disposed = true;
            entries.delete(entry);
        },
    };
}
